import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { useAuth } from "../../contexts/authContext";
import { refetchUserPermissions } from "../../hooks/useCheckTokenAndFetchUser";
import { fetchFastApi } from "../../api/fastapi";
import {
  consumePostLoginRedirect,
  safeInternalPath,
} from "../../lib/authRedirect";
import { isPartnerTermsGated } from "../../lib/partnerTerms";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";

// The RequireAuth/RequireAuthAndAuditEnabled gate stashes where the user was
// headed via router state (`state={{ from: location }}`), not the
// sessionStorage-based postLoginRedirect stash (that one is only written on
// unauthenticated bounces). Narrowly validate the shape we actually need
// (pathname + search) before trusting it, so a stray or foreign `state` value
// can't do anything unexpected.
const AcceptTermsRedirectStateSchema = z.object({
  from: z.object({
    pathname: z.string(),
    search: z.string(),
  }),
});

// The agreements hub rather than the dated PDF it links to: the hub always
// lists the current revision, so the app doesn't point at superseded terms
// when legal publishes a new dated file.
const DEVELOPER_TERMS_URL = "https://optro.ai/enterprise-agreements";

export const PartnerTermsPage = () => {
  const { workspace, workspaceMember, logout: logoutAuth, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  if (!isPartnerTermsGated(workspace, workspaceMember)) {
    // replace, not push: pushing makes Back return here and re-push /audit,
    // trapping the back button for accepted/non-Partner visitors.
    return <Navigate to="/audit" replace />;
  }

  const acceptTerms = async () => {
    let accepted = false;
    try {
      setSubmitting(true);
      // The request identifies the membership only through the X-Workspace-Id
      // header, which fetchFastApi re-reads from localStorage at send time,
      // while this page renders from the in-memory workspace. Another tab
      // switching workspaces rewrites that key, so posting anyway would accept
      // (and write the audit row) against a membership this gate never showed,
      // leaving the gated one unaccepted. Resync instead of writing blind.
      const storedWorkspaceId = localStorage.getItem("workspace_id");
      if (storedWorkspaceId !== workspace.id) {
        await refetchUserPermissions({ fresh: true });
        toast.error(
          "Your active workspace changed in another tab. Please try again.",
        );
        return;
      }
      await fetchFastApi({ method: "POST", path: "/user/accept-terms" });
      accepted = true;

      // Rehydrate synchronously, the same shape SSOCallback uses: fetch, then
      // push the parsed result into the auth context with setAuth directly,
      // rather than relying on the fire-and-forget module bridge to eventually
      // flow through another component's effect. Both guards now gate /audit on
      // acceptedTerms, so if we navigated while context still held the stale
      // `acceptedTerms: false`, the very next render would bounce straight back
      // to /accept-terms. `fresh` for the same reason: a refetch already in
      // flight when the POST resolved is a snapshot from before the acceptance.
      const rehydrated = await refetchUserPermissions({ fresh: true });
      const acceptedWorkspaceMember = rehydrated?.parsedWorkspaceMember;
      // Assert the flag, not just the object: `acceptedTerms` zod-defaults to
      // false when the backend omits it, so a presence-only check would let the
      // stale value through and the gate would bounce the user straight back
      // here with nothing on screen to explain why.
      if (!acceptedWorkspaceMember?.acceptedTerms) {
        throw new Error(
          "Failed to refresh workspace membership after accepting terms",
        );
      }
      setAuth((prev) => ({
        ...prev,
        user: rehydrated?.parsedUser ?? prev.user,
        workspace: rehydrated?.parsedWorkspace ?? prev.workspace,
        workspaceMember: acceptedWorkspaceMember,
        workspaces: rehydrated?.parsedWorkspaces ?? prev.workspaces,
      }));

      const redirectState = AcceptTermsRedirectStateSchema.safeParse(
        location.state,
      );
      // Both branches go through safeInternalPath: the schema only checks that
      // pathname and search are strings, so it accepts values like `//evil.com`
      // that the origin check rejects, and the same call applies the AUTH_PATHS
      // loop guard the postLoginRedirect branch already got for free.
      const destination = safeInternalPath(
        redirectState.success
          ? redirectState.data.from.pathname + redirectState.data.from.search
          : consumePostLoginRedirect("/audit"),
        "/audit",
      );
      navigate(destination, { replace: true });
    } catch (error) {
      console.error(error);
      // The acceptance itself and the refresh that follows it fail differently:
      // once the POST has returned, the terms ARE accepted, so telling the user
      // it failed would send them to support over what a reload fixes.
      toast.error(
        accepted
          ? "Terms accepted, but refreshing your session failed. Please reload."
          : "Failed to accept terms",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = () => {
    logoutAuth();
    navigate("/signin");
  };

  return (
    <div className="h-screen w-screen bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover overflow-y-auto">
      <div className="flex min-h-screen justify-center items-center py-12">
        <Card className="p-8 rounded-sm shadow-2xl z-10 w-full max-w-xl border border-stone-100">
          <div className="flex flex-col gap-4">
            <img src="/logo_white_bg.svg" alt="logo" className="w-20" />
            <div>
              <div className="text-2xl font-semibold">Developer Terms</div>
              <div className="text-font-secondary">
                Please review and accept the Developer Terms and Conditions
                below to continue.
              </div>
            </div>
            <p className="text-sm text-font-secondary">
              By clicking &quot;I Accept&quot;, you acknowledge and agree that
              your use of the Sandbox is governed by the{" "}
              <a
                className="underline text-blue-700"
                href={DEVELOPER_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Developer Terms and Conditions
              </a>
              .
            </p>
            <div className="flex gap-2">
              <Button loading={submitting} onClick={acceptTerms}>
                I Accept
              </Button>
              <Button variant="outline" disabled={submitting} onClick={signOut}>
                Sign out
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
